import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { LocaleProvider } from '../Context/LocaleProvider';
import Link from '../Link';
import { getLinkUrl } from '../../../utils/helpers';
import HeadingElement from '../HeadingElement';

import { StyledLinkGroup } from './styles';

/**
 * Represents a list of links with or without a visible `title`.
 * Consists of a title and a list of LinkGroup components.
 * @param {string} title required - The title of the group.
 * @param {string} ariaLabel required - The ariaLabel of the group.
 * @param {boolean} titleVisible [optional=true] - If `titleVisible` is set to false, `title` is not
visible to the user and aria-label is used.
 * @param {array} links [optional=[]] - The list of link objects.
 * @param {string} displayStyle [optional="block"] - Style of the component. Can be "block", "inline", "inline-with-title".
 * @param {string} className [optional=""] - Additinal css class.
 */
const LinkGroup = ({
  title,
  titleVisible,
  ariaLabel,
  links,
  displayStyle,
  className,
  headingLevel,
}) => {
  const locale = useContext(LocaleProvider);
  if (links.length) {
    const linkElements = links.map((link) => {
      const { id, externalLink, textLink, ariaLabel: aria } = link;
      const url = getLinkUrl(link, locale);
      const external = externalLink && externalLink !== '';

      const pageURL =
        typeof window !== 'undefined' ? window.location.pathname : null;

      const pageURLArray = pageURL ? pageURL.split('/') : [];

      const subInternalLink = link.internalLink;
      const subSlug = subInternalLink ? subInternalLink.slug : null;
      const isActive =
        (pageURL && pageURLArray[pageURLArray.length - 1] === subSlug) ||
        false;

      return (
        <li
          key={
            id ||
            String(
              `id_${Math.round(Math.random() * 1000)}${new Date().getTime()}`
            ) // It seems like links don't often return ids - just make a big gross string instead
          }
        >
          <Link
            to={url}
            ariaLabel={aria}
            external={external !== null}
            className={isActive ? 'active' : ''}
          >
            {textLink}
          </Link>
        </li>
      );
    });
    if (titleVisible) {
      const ariaId = title && title.replace(/[^a-z0-9]/g, '-');
      return (
        <StyledLinkGroup
          aria-labelledby={ariaId}
          displayStyle={displayStyle}
          className={`styledLinkGroup ${className}`}
        >
          <HeadingElement ariaId={ariaId} headingLevel={headingLevel}>
            {title}
          </HeadingElement>
          <ul>{linkElements}</ul>
        </StyledLinkGroup>
      );
    }
    return (
      <StyledLinkGroup
        aria-label={ariaLabel}
        displayStyle={displayStyle}
        className={className}
      >
        <ul>{linkElements}</ul>
      </StyledLinkGroup>
    );
  }
  return null;
};

LinkGroup.propTypes = {
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  titleVisible: PropTypes.bool,
  links: PropTypes.array,
  displayStyle: PropTypes.string,
  className: PropTypes.string,
};

LinkGroup.defaultProps = {
  title: null,
  titleVisible: true,
  ariaLabel: null,
  links: [],
  displayStyle: 'block',
  className: '',
};

export default LinkGroup;
